import * as React from 'react'
import { Icon, IconName } from '@blueprintjs/core'
import classNames from 'classnames'
import { Popover } from './popover'

/**
 * Drop-in replacement for `Menu`, `MenuItem`, and `MenuDivider` from
 * `@blueprintjs/core`.
 *
 * Goal: keep the Blueprint API surface so callsites can switch by changing the
 * import. Visuals reuse Blueprint's existing `bp4-menu*` CSS classes — that
 * stylesheet is still loaded by the app, and reproducing its full ruleset
 * (intent variants, hover/active/selected states, icon + label flexbox layout)
 * in Tailwind would be a lot of churn for visuals that already exist. Once
 * Blueprint's CSS is fully retired we can re-skin in Tailwind in one pass.
 *
 * Audit-supported props (counts from the 2026-05-08 sweep audit, 25 files):
 *
 *   MenuItem (96 instances):
 *     text                91 — required, ReactNode (often JSX, not just strings)
 *     onClick             76 — fires on activation; receives MouseEvent
 *     disabled            21 — adds bp4-disabled, suppresses onClick
 *     icon                17 — IconName string; rendered via Blueprint's <Icon />
 *     className           10 — extra utility classes on the anchor
 *     location, route      6 each — non-standard props from NavbarBottomSection
 *                                 (spread to the underlying anchor; harmless)
 *     textClassName        6 — class on the inner text span
 *     label                5 — secondary text rendered right-aligned
 *     popoverProps         2 — submenu trigger; presence of children determines submenu
 *     active               2 — used by Blueprint's <Select> renderer for keyboard-nav highlight
 *     selected             2 — sticky-blue selected state
 *     style                1
 *
 *   Menu (20 instances): className (2), style (3), and miscellaneous custom
 *     props from one local Menu component that shadowed the Blueprint name.
 *
 *   MenuDivider (11 instances): no props in use.
 *
 * Submenu pattern (MenuItem with children + popoverProps): one callsite,
 * `Filter.tsx`, renders nested MenuItems inside a parent MenuItem to produce a
 * fly-out submenu. We anchor a hover-Popover containing a Menu of the
 * children, with a chevron-right indicator. `popoverProps` is accepted but not
 * spread — the only flag passed today (`openOnTargetFocus: false`) is already
 * the default behaviour of our Popover.
 *
 * Inside `@blueprintjs/select` (4 callsites: ProfileSelect, DefaultProfileSelect,
 * CampaignSelector, PromotionItemAdd): Select calls our MenuItem from inside
 * its own popover and passes `modifiers.active` (current keyboard-focused
 * item) through as the `active` prop. Select handles keyboard navigation
 * itself — our MenuItem just needs to render the active highlight.
 */

export interface MenuProps extends React.HTMLAttributes<HTMLUListElement> {
    children?: React.ReactNode
}

export const Menu = React.forwardRef<HTMLUListElement, MenuProps>(function Menu(
    { className, children, ...rest },
    ref,
) {
    return (
        <ul ref={ref} className={classNames('bp4-menu', className)} {...rest}>
            {children}
        </ul>
    )
})

interface MenuItemPopoverProps {
    /** Currently a no-op: the only flag passed in the codebase is
     *  `openOnTargetFocus: false`, which matches our Popover default. Kept in
     *  the type for callsite parity. */
    openOnTargetFocus?: boolean
}

export interface MenuItemProps {
    text?: React.ReactNode
    /** Icon name (Blueprint IconName). Rendered via Blueprint's `<Icon />`. */
    icon?: IconName
    /** Right-aligned secondary text. */
    label?: React.ReactNode
    /** Sticky "this is the chosen value" state (intent-coloured background). */
    selected?: boolean
    /** Transient highlight (used by `<Select>` for keyboard-nav focus). */
    active?: boolean
    disabled?: boolean
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
    /**
     * When true (default), clicking this item closes the surrounding popover.
     * Matches Blueprint's MenuItem default — every action menu in the codebase
     * relies on this (e.g. EllipsisMenu's "Give coins", "Remove rule"). Set to
     * `false` for items that should keep the menu open (filter checkboxes,
     * multi-select pickers).
     */
    shouldDismissPopover?: boolean
    className?: string
    /** Class applied to the inner text span. */
    textClassName?: string
    style?: React.CSSProperties
    /** Children turn this into a submenu trigger. */
    children?: React.ReactNode
    /** Submenu Popover options. Currently a no-op — see component header. */
    popoverProps?: MenuItemPopoverProps
    /** Pass-through for navbar-tools usage; not interpreted here. */
    location?: string
    /** Pass-through for navbar-tools usage; not interpreted here. */
    route?: string
    /** id, role, aria-* etc. */
    [key: string]: unknown
}

export const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>(function MenuItem(
    {
        text,
        icon,
        label,
        selected,
        active,
        disabled,
        onClick,
        shouldDismissPopover = true,
        className,
        textClassName,
        style,
        children,
        popoverProps: _popoverProps,
        location: _location,
        route: _route,
        ...rest
    },
    ref,
) {
    // Detect "any meaningful children". Cases that must NOT render a submenu:
    //   - undefined / null
    //   - a single boolean (e.g. `{showOptions && (<>…</>)}` when showOptions
    //     is false leaves the boolean `false` as the only child)
    //   - an empty array, or an array of only falsy values (Filter.tsx-style
    //     `[showOptions && …, customFilters.map(…)]` collapses to `[false, []]`
    //     when both branches are empty)
    // `React.Children.toArray` handles all of these — it strips null,
    // undefined, and booleans, and recursively flattens arrays.
    const hasSubmenu = React.Children.toArray(children).length > 0

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (disabled) {
            event.preventDefault()
            return
        }
        onClick?.(event)
    }

    // Anchor classes match `.bp4-menu-item` plus the state modifiers Blueprint
    // toggles for active / selected / disabled.
    //
    // Blueprint's MenuItem applies BOTH `bp4-active` AND `bp4-selected` when
    // `active` is set and there's no `intent` — so the visual you see for an
    // active-but-not-explicitly-selected item is the blue selected state, not
    // the grey "pressed" active state. We don't expose an `intent` prop (no
    // callsite uses it), so the simplification is: `active` implies the same
    // visual as `selected`. See Blueprint's menuItem.js:
    //   _a[Classes.ACTIVE] = active
    //   _a[Classes.SELECTED] = selected || (active && intentClass === undefined)
    //
    // `bp4-popover-dismiss` is the auto-close hook that Blueprint's MenuItem
    // applies by default. Our Popover listens for clicks on this class inside
    // its content and closes itself — so action menus dismiss after the user
    // picks an item without each callsite having to wire up onClose. Items
    // that are submenu triggers or disabled don't dismiss (matches Blueprint).
    const anchorClassName = classNames(
        'bp4-menu-item',
        {
            'bp4-active': active && !disabled,
            'bp4-selected': selected || (active && !disabled),
            'bp4-disabled': disabled,
            'bp4-popover-dismiss': shouldDismissPopover && !disabled && !hasSubmenu,
        },
        className,
    )

    const anchor = (
        <a
            className={anchorClassName}
            style={style}
            onClick={handleClick}
            // Blueprint sets aria-disabled on the anchor and removes the href.
            // We don't render an href to begin with, so just mirror the aria.
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            role="menuitem"
            {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
            {/* Icon slot. Blueprint reserves space when icon is absent so item
                rows align across a menu where some items have icons; we mirror
                that by rendering an empty span with the icon class. */}
            {icon !== undefined && icon !== null ? (
                <Icon icon={icon} className="bp4-menu-item-icon" />
            ) : null}
            <div className={classNames('bp4-fill', textClassName)}>{text}</div>
            {label !== undefined && label !== null ? (
                <span className="bp4-menu-item-label">{label}</span>
            ) : null}
            {/* No submenu chevron. Blueprint renders one, but the only
                submenu in this codebase (Filter.tsx "Saved Filters") reads
                better without — and on hover the fly-out itself is a clear
                enough indicator. */}
        </a>
    )

    // When the item has children, wrap the anchor in a hover-Popover that
    // displays a Menu of the children to the right — Blueprint's submenu
    // pattern. The trigger is the same anchor that renders without children;
    // its caret indicates the submenu.
    const content = hasSubmenu ? (
        <Popover
            interactionKind="hover"
            position="right-top"
            // `minimal` drops the arrow + heavier shadow. A submenu's arrow
            // points back into the parent item, which crowds the parent's
            // text/icon — Blueprint's submenus don't show an arrow either.
            minimal
            content={<Menu>{children}</Menu>}
        >
            {anchor}
        </Popover>
    ) : (
        anchor
    )

    return (
        <li ref={ref} role="none">
            {content}
        </li>
    )
})

export interface MenuDividerProps {
    /** Optional section title. When provided, Blueprint renders the divider as
     *  a small uppercase header instead of a hairline rule. */
    title?: React.ReactNode
    className?: string
}

export const MenuDivider: React.FC<MenuDividerProps> = ({ title, className }) => {
    if (title !== undefined && title !== null) {
        return (
            <li className={classNames('bp4-menu-header', className)}>
                <h6 className="bp4-heading">{title}</h6>
            </li>
        )
    }
    return <li className={classNames('bp4-menu-divider', className)} />
}
